import { useState } from 'react';
import { motion } from 'framer-motion';
import { Wrench, FileText, ChevronDown, ChevronUp } from 'lucide-react';
import type { ServiceTask } from '../../types/proposal';
import { formatCurrency } from '../../utils';
import { ImageGallery } from '../common/ImageGallery';
import { TaskLineItems } from './TaskLineItems';
import { TaskAcceptanceToggle } from './TaskAcceptanceToggle';

interface TaskCardProps {
  task: ServiceTask;
  index: number;
  showLineDetails: boolean;
  onAccept: (lineId: string) => void;
  onDecline: (lineId: string, reason?: string) => void;
}

export function TaskCard({
  task,
  index,
  showLineDetails,
  onAccept,
  onDecline,
}: TaskCardProps) {
  const [showDeclineReason, setShowDeclineReason] = useState(false);
  const [declineReason, setDeclineReason] = useState(task.declinedReason || '');
  const [expandedDetails, setExpandedDetails] = useState(false);

  const hasMedia = (task.images && task.images.length > 0) || task.videoUrl;
  const hasNotes = task.notes && task.notes.trim().length > 0;
  const hasLineItems = showLineDetails && task.lineItems && task.lineItems.length > 0;

  const getStatusColor = () => {
    if (task.accepted) return 'border-green-500 bg-green-50/50';
    if (task.declined) return 'border-red-500 bg-red-50/50';
    return 'border-gray-200 bg-white';
  };

  const handleAccept = () => {
    setShowDeclineReason(false);
    onAccept(task.lineId);
  };

  const handleDecline = () => {
    if (!task.declined) {
      setShowDeclineReason(true);
    } else {
      onDecline(task.lineId);
    }
  };

  const submitDecline = () => {
    onDecline(task.lineId, declineReason);
    setShowDeclineReason(false);
  };

  return (
    <motion.div
      className={`rounded-2xl border-2 shadow-lg overflow-hidden ${getStatusColor()}`}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
    >
      {/* Task Header */}
      <div className="bg-hul-navy p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
            <Wrench className="w-5 h-5 text-white" />
          </div>
          <div>
            <div className="text-white/60 text-xs uppercase tracking-wider font-mono">
              {task.taskCode}
            </div>
            <h3 className="text-white font-heading text-lg tracking-wide">
              {task.description}
            </h3>
          </div>
        </div>
        <div className="text-right">
          <div className="text-white/60 text-xs uppercase tracking-wider">
            Task Total
          </div>
          <div className="text-white font-mono text-2xl font-bold">
            {formatCurrency(task.price)}
          </div>
        </div>
      </div>

      {/* Task Content */}
      <div className="p-6">
        {/* Media Section */}
        {hasMedia && (
          <div className="mb-6">
            <ImageGallery
              images={task.images || []}
              alt={task.description}
              videoUrl={task.videoUrl}
            />
          </div>
        )}

        {/* Notes Section */}
        {hasNotes && (
          <div className="mb-6">
            <div className="flex items-center gap-2 text-gray-400 text-xs uppercase tracking-wider mb-2">
              <FileText className="w-4 h-4" />
              Technician Notes
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border-l-4 border-hul-red">
              <p className="text-gray-700 whitespace-pre-line leading-relaxed">
                {task.notes}
              </p>
            </div>
          </div>
        )}

        {/* Line Items (if showLineDetails is true) */}
        {hasLineItems && (
          <div className="mb-6">
            <button
              onClick={() => setExpandedDetails(!expandedDetails)}
              className="flex items-center gap-2 text-gray-500 hover:text-hul-navy transition-colors text-sm"
            >
              {expandedDetails ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
              <span className="font-heading uppercase tracking-wider">
                {expandedDetails ? 'Hide Details' : 'Show Line Items'}
              </span>
              <span className="text-gray-400">
                ({task.lineItems!.length} items)
              </span>
            </button>

            {expandedDetails && (
              <TaskLineItems lineItems={task.lineItems!} />
            )}
          </div>
        )}

        {/* Acceptance Controls */}
        <div className="pt-4 border-t border-gray-100">
          <TaskAcceptanceToggle
            accepted={task.accepted}
            declined={task.declined}
            onAccept={handleAccept}
            onDecline={handleDecline}
          />

          {/* Decline Reason Input */}
          {showDeclineReason && !task.declined && (
            <motion.div
              className="mt-4"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              transition={{ duration: 0.2 }}
            >
              <label className="block text-sm text-gray-600 mb-2">
                Please let us know why you're declining this task (optional):
              </label>
              <textarea
                value={declineReason}
                onChange={(e) => setDeclineReason(e.target.value)}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-hul-red/20 focus:border-hul-red resize-none"
                rows={2}
                placeholder="e.g., Budget constraints, will address later..."
              />
              <div className="flex gap-3 mt-3">
                <button
                  onClick={submitDecline}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg font-heading uppercase tracking-wider text-sm hover:bg-red-700 transition-colors"
                >
                  Confirm Decline
                </button>
                <button
                  onClick={() => setShowDeclineReason(false)}
                  className="px-4 py-2 bg-gray-100 text-gray-600 rounded-lg font-heading uppercase tracking-wider text-sm hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          {/* Show decline reason if already declined */}
          {task.declined && task.declinedReason && (
            <motion.div
              className="mt-4 p-3 bg-red-50 rounded-lg border border-red-100"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
            >
              <div className="text-xs text-red-600 uppercase tracking-wider mb-1">
                Decline Reason
              </div>
              <p className="text-red-700 text-sm">{task.declinedReason}</p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Status Badge */}
      {(task.accepted || task.declined) && (
        <motion.div
          className={`py-2 text-center text-sm font-heading uppercase tracking-wider ${
            task.accepted
              ? 'bg-green-600 text-white'
              : 'bg-red-600 text-white'
          }`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
          {task.accepted ? 'Task Approved' : 'Task Declined'}
        </motion.div>
      )}
    </motion.div>
  );
}
